import Phaser from 'phaser';
import { CANVAS_W, CANVAS_H } from '../types';
import { GameSceneData } from './GameScene';
import { INTRO_LINES, WORLD_STORY, interpolate } from '../config/story';
import { AudioManager } from '../systems/AudioManager';

export type StoryKind = 'intro' | 'world-intro' | 'world-outro';

export interface StorySceneData {
  kind: StoryKind;
  /** For world-intro / world-outro */
  world?: number;
  /** Where to go after this story ends */
  next: { scene: string; data: GameSceneData | StorySceneData };
}

const TYPE_SPEED_MS = 40;

export class StoryScene extends Phaser.Scene {
  private payload!: StorySceneData;
  private lines: string[] = [];
  private lineIndex = 0;
  private charIndex = 0;
  private timer?: Phaser.Time.TimerEvent;
  private displayedText!: Phaser.GameObjects.Text;
  private hint!: Phaser.GameObjects.Text;
  private done = false;

  constructor() { super({ key: 'StoryScene' }); }

  init(data: StorySceneData) {
    this.payload = data;
  }

  create() {
    this.cameras.main.setBackgroundColor('#131120');

    // Build the lines based on kind + interpolate names. The chained next.data
    // may be a GameSceneData OR another StorySceneData; unwrap until we find
    // the GameSceneData with player names.
    const findNames = (d: GameSceneData | StorySceneData): { p1: string; p2: string } => {
      if ('p1' in d && 'p2' in d) return { p1: d.p1.name, p2: d.p2.name };
      return findNames(d.next.data);
    };
    const { p1, p2 } = findNames(this.payload.next.data);
    let raw: string[] = [];
    let title = '';
    if (this.payload.kind === 'intro') {
      raw = INTRO_LINES;
      title = '— Capitolul 0 —';
    } else if (this.payload.kind === 'world-intro') {
      raw = WORLD_STORY[this.payload.world!]?.intro ?? [];
      title = `— Lume ${this.payload.world} —`;
    } else {
      raw = WORLD_STORY[this.payload.world!]?.outro ?? [];
      title = `— Sfârșit Lume ${this.payload.world} —`;
    }
    this.lines = raw.map(l => interpolate(l, p1, p2));

    // BGM transitions: silence during world-outro, play new world's BGM during world-intro
    if (this.payload.kind === 'world-outro') {
      AudioManager.stopBgm();
    } else if (this.payload.kind === 'world-intro' && this.payload.world != null) {
      AudioManager.startBgmForWorld(this.payload.world);
    }

    // Title
    this.add.text(CANVAS_W / 2, 80, title, {
      fontFamily: 'monospace', fontSize: '20px', color: '#06d6a0',
    }).setOrigin(0.5);

    // Body text area
    this.displayedText = this.add.text(CANVAS_W / 2, CANVAS_H / 2, '', {
      fontFamily: 'monospace',
      fontSize: '20px',
      color: '#fff',
      align: 'center',
      wordWrap: { width: CANVAS_W - 100 },
      lineSpacing: 12,
    }).setOrigin(0.5);

    this.hint = this.add.text(CANVAS_W / 2, CANVAS_H - 50,
      'SPACE = mai departe • ENTER = skip tot',
      { fontFamily: 'monospace', fontSize: '12px', color: '#666' }
    ).setOrigin(0.5);

    // Start typing
    this.startNextLine();

    const kb = this.input.keyboard!;
    kb.on('keydown-SPACE', () => this.advance());
    kb.on('keydown-Q',     () => this.advance());
    kb.on('keydown-ENTER', () => this.skipAll());
    kb.on('keydown-ESC',   () => this.finish());
    this.input.on('pointerdown', () => this.advance());
  }

  private startNextLine() {
    this.charIndex = 0;
    this.timer?.remove();
    this.timer = this.time.addEvent({
      delay: TYPE_SPEED_MS,
      loop: true,
      callback: () => this.typeChar(),
    });
  }

  private typeChar() {
    if (this.lineIndex >= this.lines.length) {
      this.timer?.remove();
      this.done = true;
      return;
    }
    const line = this.lines[this.lineIndex];
    if (this.charIndex < line.length) {
      const cur = this.displayedText.text;
      // Append next char; on word boundary play a soft tick
      const nextCh = line.charAt(this.charIndex);
      this.displayedText.setText(cur + nextCh);
      this.charIndex++;
      if (nextCh === '.' || nextCh === '!' || nextCh === '?') {
        // tiny pause after punctuation
        this.timer?.remove();
        this.time.delayedCall(180, () => this.startNextLine_continue());
      }
    } else {
      // Line complete; append newline and start next line
      this.displayedText.setText(this.displayedText.text + '\n');
      this.lineIndex++;
      this.charIndex = 0;
    }
  }

  private startNextLine_continue() {
    // Resume typing after a punctuation pause
    this.timer = this.time.addEvent({
      delay: TYPE_SPEED_MS,
      loop: true,
      callback: () => this.typeChar(),
    });
  }

  private advance() {
    if (this.done) { this.finish(); return; }
    // If the current line isn't fully typed, complete it instantly
    if (this.lineIndex < this.lines.length) {
      const line = this.lines[this.lineIndex];
      if (this.charIndex < line.length) {
        // Reconstruct from lines array instead of slicing display text (safer with newlines)
        const prefix = this.lines.slice(0, this.lineIndex).join('\n') + (this.lineIndex > 0 ? '\n' : '');
        this.displayedText.setText(prefix + line + '\n');
        this.charIndex = line.length;
        this.lineIndex++;
        this.timer?.remove();
        this.startNextLine();
        return;
      }
    }
    // Otherwise advance to next line
    this.lineIndex++;
    if (this.lineIndex >= this.lines.length) {
      this.done = true;
      this.timer?.remove();
    }
  }

  private skipAll() {
    this.timer?.remove();
    this.displayedText.setText(this.lines.join('\n'));
    this.done = true;
  }

  private finish() {
    AudioManager.sfx('menu-select');
    this.scene.start(this.payload.next.scene, this.payload.next.data);
  }
}
